import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/**
 * The `entityTenant` value meaning "no entity selected" — the unscoped platform
 * view, shown in the label menu as "ServEngine".
 *
 * Absence is the sentinel on purpose. It is the store's initial value, so a fresh
 * or cleared browser defaults to unscoped rather than to whichever entity happens
 * to sort first, and `axiosInstance` sends no `acting-entity-id` header at all in
 * this state — so if the header is ever dropped by a proxy the result is the same
 * unscoped view the UI is already claiming, never a silent narrowing.
 */
export const SERVENGINE_SCOPE = "";

/**
 * The platform tenant: a real `entity` row (seeded as "default_tenant") that owns
 * no work orders, clients or invoices. It comes back from the entity dropdown
 * like any other entity, so it is filtered out of the label menu — selecting it
 * would scope every list to nothing and read as data loss. "ServEngine" is the
 * correct way to express that state, and it maps to SERVENGINE_SCOPE.
 */
export const PLATFORM_TENANT_ID = "00000000-0000-7000-8000-000000000002";

export const isEntityScoped = (entityTenant: string): boolean =>
  entityTenant !== SERVENGINE_SCOPE && entityTenant !== PLATFORM_TENANT_ID;

interface EntityTenantStore {
  entityTenant: string;
  setEntityTenant: (entityTenant: string) => void;
}

export const useEntityTenantStore = create<EntityTenantStore>()(
  persist(
    (set) => ({
      entityTenant: SERVENGINE_SCOPE,
      setEntityTenant: (entityTenant) => set({ entityTenant }),
    }),
    {
      name: "entity-tenant",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
